import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, ButtonBase, Checkbox, FormControlLabel, IconButton, InputAdornment, Link as MuiLink,
    List, ListItemButton, ListItemText, MenuItem, Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailEditor from 'react-email-editor';

const LANGS = [
    { code: 'nl', short: 'NL', label: 'Dutch'   },
    { code: 'en', short: 'GB', label: 'English' },
    { code: 'fr', short: 'FR', label: 'French'  },
];

// Where the page surfaces in the navigation. Mirrors the old admin's
// "Positie" dropdown — Hidden means the page exists at a URL but isn't
// linked from any menu/footer.
const POSITIONS = [
    { value: 'hidden',     i18nKey: 'cfg_dyn_pos_hidden',     fallback: 'Hidden'                  },
    { value: 'menu',       i18nKey: 'cfg_dyn_pos_menu',       fallback: 'Main menu'               },
    { value: 'footer',     i18nKey: 'cfg_dyn_pos_footer',     fallback: 'Footer'                  },
    { value: 'topbar',     i18nKey: 'cfg_dyn_pos_topbar',     fallback: 'Top bar'                 },
    { value: 'extraFooter',i18nKey: 'cfg_dyn_pos_extra',      fallback: 'Extra footer'            },
];

// Sub-placement (which slot inside the chosen Position). Old admin called
// this "Extra" — kept for parity even though most pages don't use it.
const EXTRA_PLACEMENTS = [
    { value: '',        i18nKey: 'cfg_dyn_extra_none',       fallback: '— none —'           },
    { value: 'primary', i18nKey: 'cfg_dyn_extra_primary',    fallback: 'Primary slot'       },
    { value: 'second',  i18nKey: 'cfg_dyn_extra_secondary',  fallback: 'Secondary slot'     },
];

// Convert a free-typed page title to a URL-safe slug. Mirrors the old admin
// behaviour: lower-cased, ASCII-only, dashes between words.
const slugify = (s) =>
    String(s || '')
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);

const blankPage = (i = 1) => ({
    id:       `p_${Date.now()}_${i}`,
    slug:     `dyn-page-${i}`,
    position: 'hidden',
    extra:    '',
    isActive: true,
    iframe:   false,
    titles:   { nl: `Dyn. page ${i}`, en: '', fr: '' },
    designs:  { nl: null, en: null, fr: null },  // unlayer JSON designs
});

const DynamicPageTab = ({ theme, t, baseUrl = 'https://testshop.fashionretail.be' }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [pages, setPages] = useState([
        blankPage(1),
        blankPage(2),
        blankPage(3),
    ]);
    const [activeId, setActiveId] = useState(pages[0]?.id);
    const [langTab, setLangTab]   = useState(0);

    const active = pages.find(p => p.id === activeId);
    const activeLang = LANGS[langTab].code;

    const updateActive = (patch) => setPages(prev => prev.map(p => p.id === activeId ? { ...p, ...patch } : p));
    const updateActiveTitle = (lang, val) => updateActive({ titles: { ...(active?.titles || {}), [lang]: val } });

    const addPage = () => {
        const next = blankPage(pages.length + 1);
        setPages(prev => [...prev, next]);
        setActiveId(next.id);
    };
    const removePage = () => {
        if (!active) return;
        const remaining = pages.filter(p => p.id !== activeId);
        setPages(remaining);
        setActiveId(remaining[0]?.id);
    };

    const copyLayoutFrom = (fromLang) => {
        if (!active) return;
        if (fromLang === activeLang) return;
        const src = active.designs?.[fromLang];
        if (!src) return;
        updateActive({ designs: { ...(active.designs || {}), [activeLang]: src } });
        // load into the editor immediately
        if (editorRef.current?.editor) {
            try { editorRef.current.editor.loadDesign(src); } catch { /* ignore */ }
        }
    };

    // ────── Unlayer editor wiring ──────
    // One editor instance per page. When the active page or language
    // changes, we save the current design back to state and load the
    // target design into the editor.
    const editorRef = useRef(null);

    // Save handler: pull current design from the editor and store it.
    const persistDesign = (pageId, lang) => new Promise((resolve) => {
        const ed = editorRef.current?.editor;
        if (!ed) return resolve();
        try {
            ed.saveDesign((design) => {
                setPages(prev => prev.map(p => p.id === pageId
                    ? { ...p, designs: { ...(p.designs || {}), [lang]: design } }
                    : p));
                resolve();
            });
        } catch { resolve(); }
    });

    // Load the design for the active page+lang when it changes. We track the
    // previously-active page+lang via a ref so the persist-then-load order
    // isn't lost across renders.
    const prevRef = useRef({ pageId: activeId, lang: activeLang });
    useEffect(() => {
        const ed = editorRef.current?.editor;
        if (!ed || !active) return;
        const prev = prevRef.current;
        const next = { pageId: activeId, lang: activeLang };

        const loadCurrent = () => {
            const design = active.designs?.[activeLang];
            try {
                if (design) ed.loadDesign(design);
                else        ed.loadBlank?.();
            } catch { /* ignore */ }
        };

        if (prev.pageId !== next.pageId || prev.lang !== next.lang) {
            persistDesign(prev.pageId, prev.lang).finally(loadCurrent);
        }
        prevRef.current = next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId, activeLang]);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 3, alignItems: 'start' }}>

            {/* ─────────── Master sidebar ─────────── */}
            <Box>
                <Box sx={{ px: 1, mb: 1 }}>
                    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                        {tr('cfg_dyn_list_title', 'Dynamic pages')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {pages.length} {tr('cfg_dyn_pages_count', 'pages')} · {pages.filter(p => p.isActive).length} {tr('cfg_dyn_pages_active', 'active')}
                    </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mx: 1, mb: 1 }} />
                <List dense disablePadding>
                    {pages.map((p) => (
                        <ListItemButton
                            key={p.id}
                            selected={p.id === activeId}
                            onClick={() => setActiveId(p.id)}
                            sx={{
                                borderRadius: 1.5, mb: 0.5, py: 1.25,
                                '&.Mui-selected':       { backgroundColor: 'rgba(25,118,210,0.08)' },
                                '&.Mui-selected:hover': { backgroundColor: 'rgba(25,118,210,0.12)' },
                            }}
                        >
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: p.isActive ? 'success.main' : 'grey.400',
                                mr: 1.5, flexShrink: 0,
                            }} />
                            <ListItemText
                                primary={
                                    <Typography variant="body2" sx={{
                                        fontWeight: p.id === activeId ? 700 : 500,
                                        color:      p.id === activeId ? 'primary.main' : 'text.primary',
                                    }}>
                                        {p.titles?.nl || tr('cfg_dyn_untitled', 'Untitled page')}
                                    </Typography>
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>
                <ButtonBase
                    onClick={addPage}
                    sx={{
                        mt: 1, width: '100%', py: 1.5, borderRadius: 1.5,
                        border: '1.5px dashed', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                        color: 'text.secondary',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                    }}
                >
                    <AddIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tr('cfg_dyn_new', 'New dynamic page')}
                    </Typography>
                </ButtonBase>
            </Box>

            {/* ─────────── Detail panel ─────────── */}
            {!active ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_dyn_empty_select', 'Select a page from the list, or add a new one to get started.')}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2.5}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {active.titles?.nl || tr('cfg_dyn_untitled', 'Untitled page')}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined" color="error" size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={removePage}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            {tr('cfg_dyn_delete', 'Delete page')}
                        </Button>
                    </Box>

                    {/* URL slug */}
                    <Section
                        title={tr('cfg_dyn_url_title', 'URL slug')}
                        description={tr('cfg_dyn_url_desc', 'The name visible in the URL. (Same across all languages.)')}
                    >
                        <Row
                            label={tr('cfg_dyn_url_slug', 'URL slug')}
                            help={
                                <MuiLink
                                    href={`${baseUrl}/extra/${active.slug}`}
                                    target="_blank" rel="noopener"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
                                >
                                    {`${baseUrl}/extra/${active.slug}`}
                                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                                </MuiLink>
                            }
                            control={
                                <TextField
                                    size="small"
                                    value={active.slug}
                                    onChange={(e) => updateActive({ slug: slugify(e.target.value) })}
                                    InputProps={{ startAdornment: <InputAdornment position="start">/extra/</InputAdornment> }}
                                    sx={{ width: 280 }}
                                />
                            }
                        />
                    </Section>

                    {/* Page title — position + 3 lang inputs */}
                    <Section
                        title={tr('cfg_dyn_title_card', 'Page title')}
                        description={tr('cfg_dyn_title_card_desc', 'The title of the page. Visible in the header, footer, and as the page heading.')}
                    >
                        <Row
                            label={tr('cfg_dyn_position', 'Position')}
                            help={tr('cfg_dyn_position_help', 'Where the page link surfaces. Hidden = exists at the URL but not linked anywhere.')}
                            control={
                                <TextField
                                    select size="small"
                                    value={active.position}
                                    onChange={(e) => updateActive({ position: e.target.value })}
                                    sx={{ width: 200 }}
                                >
                                    {POSITIONS.map(p => (
                                        <MenuItem key={p.value} value={p.value}>
                                            {tr(p.i18nKey, p.fallback)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            }
                        />
                        <Row
                            label={tr('cfg_dyn_extra_slot', 'Sub-slot')}
                            help={tr('cfg_dyn_extra_slot_help', 'Optional secondary placement within the chosen position.')}
                            control={
                                <TextField
                                    select size="small"
                                    value={active.extra}
                                    onChange={(e) => updateActive({ extra: e.target.value })}
                                    sx={{ width: 200 }}
                                >
                                    {EXTRA_PLACEMENTS.map(p => (
                                        <MenuItem key={p.value || 'none'} value={p.value}>
                                            {tr(p.i18nKey, p.fallback)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            }
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                            {LANGS.map(l => (
                                <TextField
                                    key={l.code}
                                    size="small"
                                    label={
                                        <Box component="span">
                                            <Box component="span" sx={{ color: 'text.disabled', mr: 0.5 }}>{l.short}</Box>
                                            {tr(`cfg_dyn_title_${l.code}`, l.label)}
                                        </Box>
                                    }
                                    value={active.titles?.[l.code] || ''}
                                    onChange={(e) => updateActiveTitle(l.code, e.target.value)}
                                    placeholder={active.titles?.nl || ''}
                                    InputLabelProps={{ shrink: true }}
                                />
                            ))}
                        </Box>
                    </Section>

                    {/* Settings */}
                    <Section
                        title={tr('cfg_dyn_settings_title', 'Settings')}
                        description={tr('cfg_dyn_settings_desc', 'Activate the page and choose how it is rendered.')}
                    >
                        <Row
                            label={tr('cfg_dyn_active', 'Page is active')}
                            help={tr('cfg_dyn_active_help', 'When off, the page is not reachable on the storefront.')}
                            control={
                                <Switch
                                    color="success" size="small"
                                    checked={!!active.isActive}
                                    onChange={(e) => updateActive({ isActive: e.target.checked })}
                                />
                            }
                        />
                        <Row
                            label={tr('cfg_dyn_iframe', 'Render the page in an iframe')}
                            help={tr('cfg_dyn_iframe_help', 'Recommended when the content uses advanced styling or scripts that should not interfere with the rest of the shop.')}
                            control={
                                <Switch
                                    color="success" size="small"
                                    checked={!!active.iframe}
                                    onChange={(e) => updateActive({ iframe: e.target.checked })}
                                />
                            }
                        />
                    </Section>

                    {/* Content */}
                    <Section
                        title={tr('cfg_dyn_content_title', 'Page content')}
                        description={tr('cfg_dyn_content_desc', 'The body of the page. Each language has its own layout — copy from another language with the buttons on the right.')}
                        divider={false}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <Tabs
                                value={langTab}
                                onChange={(_e, v) => setLangTab(v)}
                                sx={{
                                    minHeight: 36,
                                    '& .MuiTab-root': { minHeight: 36, px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.85rem', minWidth: 0 },
                                    '& .MuiTabs-indicator': { height: 2 },
                                }}
                            >
                                {LANGS.map(l => (
                                    <Tab
                                        key={l.code}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 0.5 }}>{l.short}</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{l.label}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_dyn_layout_from', 'Copy layout from:')}
                                </Typography>
                                {LANGS.filter((_, i) => i !== langTab).map(l => (
                                    <Button
                                        key={l.code}
                                        size="small" variant="outlined"
                                        onClick={() => copyLayoutFrom(l.code)}
                                        sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 0, px: 1.25 }}
                                    >
                                        {l.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{
                            border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                            overflow: 'hidden', minHeight: 480,
                        }}>
                            <EmailEditor
                                ref={editorRef}
                                minHeight="480px"
                                onLoad={() => {
                                    const ed = editorRef.current?.editor;
                                    const design = active.designs?.[activeLang];
                                    if (ed && design) {
                                        try { ed.loadDesign(design); } catch { /* ignore */ }
                                    }
                                }}
                            />
                        </Box>
                    </Section>
                </Stack>
            )}
        </Box>
    );
};
DynamicPageTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, baseUrl: PropTypes.string };

// ────────────────────────────────────────────────────────────────────────
// Section + Row helpers (mirror BannersTab's local helpers so the visual
// language is identical without dragging in a shared file)
// ────────────────────────────────────────────────────────────────────────

const Section = ({ title, description, children, divider = true }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {description && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{description}</Typography>}
        </Box>
        <Stack
            spacing={1.5}
            divider={divider ? <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} /> : null}
        >
            {children}
        </Stack>
    </Paper>
);
Section.propTypes = { title: PropTypes.node, description: PropTypes.node, children: PropTypes.node, divider: PropTypes.bool };

const Row = ({ label, help, control, align = 'center' }) => (
    <Box sx={{ display: 'flex', alignItems: align === 'top' ? 'flex-start' : 'center', gap: 2, py: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {help && <Typography variant="caption" component="div" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);
Row.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node, align: PropTypes.oneOf(['top', 'center']) };

export default DynamicPageTab;
